import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
  WithFieldValue,
} from 'firebase/firestore'
import {
  InstallationNeedKey,
  InstallationNeedPhoto,
  InstallationNeedsAssignedAt,
  InstallationNeedsPhotos,
  INSTALLATION_NEED_KEYS,
  TransportTruck,
  TransportTruckStatus,
  TransportTruckStatusHistoryEntry,
} from '../logistics/logistics'

const VALID_STATUSES = ['needed', 'accepted', 'installed'] as const

function parseInstallationStatus(raw: unknown): TransportTruck['installation_status'] {
  if (!raw || typeof raw !== 'object') return undefined
  const obj = raw as Record<string, unknown>
  const result: TransportTruck['installation_status'] = {}
  for (const key of INSTALLATION_NEED_KEYS) {
    const v = obj[key]
    if (VALID_STATUSES.includes(v as (typeof VALID_STATUSES)[number])) {
      result[key] = v as (typeof VALID_STATUSES)[number]
    }
  }
  return Object.keys(result).length ? result : undefined
}

function parseAssignedAt(raw: unknown): InstallationNeedsAssignedAt | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const obj = raw as Record<string, unknown>
  const result: InstallationNeedsAssignedAt = {}
  for (const key of INSTALLATION_NEED_KEYS) {
    const v = obj[key]
    if (v != null) result[key] = v as Timestamp
  }
  return Object.keys(result).length ? result : undefined
}

function parseNeedPhotos(raw: unknown): InstallationNeedsPhotos | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const obj = raw as Record<string, unknown>
  const result: InstallationNeedsPhotos = {}
  for (const key of INSTALLATION_NEED_KEYS) {
    const list = obj[key]
    if (!Array.isArray(list)) continue
    const photos: InstallationNeedPhoto[] = []
    for (const entry of list) {
      if (!entry || typeof entry !== 'object') continue
      const p = entry as Record<string, unknown>
      if (typeof p.storage_path !== 'string' || !p.storage_path) continue
      photos.push({
        storage_path: p.storage_path,
        url: typeof p.url === 'string' ? p.url : undefined,
        uploaded_at: (p.uploaded_at as Timestamp) ?? Timestamp.now(),
        uploaded_by: typeof p.uploaded_by === 'string' ? p.uploaded_by : '',
        file_name: typeof p.file_name === 'string' ? p.file_name : undefined,
      })
    }
    if (photos.length) result[key as InstallationNeedKey] = photos
  }
  return Object.keys(result).length ? result : undefined
}

const VALID_TRUCK_STATUSES: TransportTruckStatus[] = [
  'draft',
  'assigned',
  'awaiting_transport',
  'in_transit',
  'repair',
  'delivered',
]

function parseStatusHistory(raw: unknown): TransportTruckStatusHistoryEntry[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const result: TransportTruckStatusHistoryEntry[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const obj = entry as Record<string, unknown>
    const status = obj.status
    const entered_at = obj.entered_at
    if (
      VALID_TRUCK_STATUSES.includes(status as TransportTruckStatus) &&
      entered_at != null
    ) {
      result.push({
        status: status as TransportTruckStatus,
        entered_at: entered_at as Timestamp,
      })
    }
  }
  return result.length ? result : undefined
}

export const transportTruckConverter: FirestoreDataConverter<TransportTruck> = {
  toFirestore(truck: WithFieldValue<TransportTruck>) {
    return {
      vehicle_id: truck.vehicle_id,
      vin: truck.vin,
      currentLocationId: truck.currentLocationId ?? null,
      status_history: Array.isArray(truck.status_history)
        ? (truck.status_history as TransportTruckStatusHistoryEntry[]).map((e) => ({
            status: e.status,
            entered_at: e.entered_at ?? Timestamp.now(),
          }))
        : [],
      installation_status: truck.installation_status ?? null,
      installation_needs_assigned_company: truck.installation_needs_assigned_company ?? null,
      installation_needs_assigned_at: truck.installation_needs_assigned_at ?? null,
      installation_needs_hidden: truck.installation_needs_hidden ?? false,
      installation_needs_hidden_at: truck.installation_needs_hidden_at ?? null,
      installation_needs_hidden_by: truck.installation_needs_hidden_by ?? null,
      installation_needs_force_completed: truck.installation_needs_force_completed ?? false,
      installation_needs_force_completed_at: truck.installation_needs_force_completed_at ?? null,
      installation_needs_force_completed_by: truck.installation_needs_force_completed_by ?? null,
      installation_needs_photos: truck.installation_needs_photos ?? null,
      created_at: truck.created_at ?? Timestamp.now(),
      created_by: truck.created_by ?? '',
      updated_at: truck.updated_at ?? Timestamp.now(),
    }
  },

  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ): TransportTruck {
    const data = snapshot.data(options)
    const statusHistory = parseStatusHistory(data.status_history)
    return {
      id: snapshot.id,
      vehicle_id: data.vehicle_id ?? '',
      vin: data.vin ?? '',
      currentLocationId: data.currentLocationId ?? undefined,
      status_history: statusHistory,
      installation_status: parseInstallationStatus(data.installation_status),
      installation_needs_assigned_company: data.installation_needs_assigned_company ?? undefined,
      installation_needs_assigned_at: parseAssignedAt(data.installation_needs_assigned_at),
      installation_needs_hidden: data.installation_needs_hidden === true,
      installation_needs_hidden_at: data.installation_needs_hidden_at ?? null,
      installation_needs_hidden_by: data.installation_needs_hidden_by ?? null,
      installation_needs_force_completed: data.installation_needs_force_completed === true,
      installation_needs_force_completed_at: data.installation_needs_force_completed_at ?? null,
      installation_needs_force_completed_by: data.installation_needs_force_completed_by ?? null,
      installation_needs_photos: parseNeedPhotos(data.installation_needs_photos),
      created_at: data.created_at ?? Timestamp.now(),
      created_by: data.created_by ?? '',
      updated_at: data.updated_at ?? Timestamp.now(),
    }
  },
}
